import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import CompanyHeader from '@/components/company/CompanyHeader';
import FormBuilderWithLayout from '@/components/company/FormBuilderWithLayout';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import useKeyActivityState, { IKeyActivity } from '@/states/key-activities.state';
import useTenantState from '@/states/tenant.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import useOrgChartState from '@/states/organization.state';
import SearchBar from '@/components/company/filters/SearchBar';
import FilterSelect from '@/components/company/filters/FilterSelect';
import ClearFilterButton from '@/components/company/filters/ClearFilterButton';

function getPublishedFilter(selectedStatus: string): boolean | undefined {
  if (selectedStatus === 'Published') return true;
  if (selectedStatus === 'Unpublished') return false;
  return undefined;
}

export default function KeyActivities() {
  const formControl = useForm<IKeyActivity>({ defaultValues: { name: '' } });
  const { handleSubmit, reset } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const {
    keyActivities,
    paginatedData,
    getKeyActivities,
    createKeyActivity,
    updateKeyActivity,
    deleteKeyActivity,
    bulkPublishKeyActivities,
    isLoading,
  } = useKeyActivityState();

  const { currentTenant } = useTenantState();
  const { orgCharts, fetchOrgChartTree } = useOrgChartState();

  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<IKeyActivity | null>(null);
  const [allPublished, setAllPublished] = useState(false);
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'name',
    order: 'asc',
  });

  const flattenTree = (
    nodes: { id: number; name: string; children?: unknown[] }[],
  ): { id: number; name: string; children?: unknown[] }[] => {
    const result: { id: number; name: string; children?: unknown[] }[] = [];
    nodes.forEach(node => {
      result.push(node);
      if (node.children?.length) {
        result.push(...flattenTree(node.children as { id: number; name: string; children?: unknown[] }[]));
      }
    });
    return result;
  };

  useEffect(() => {
    setAllPublished(keyActivities.length > 0 && keyActivities.every(item => item.is_published === true));
  }, [keyActivities]);

  // Fetch data with server-side pagination and filters
  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    try {
      await fetchOrgChartTree(currentTenant.id);
      await getKeyActivities(currentTenant.id, {
        page: pageConfig.page,
        page_size: pageConfig.limit,
        org_chart_node_id: selectedRole ? Number(selectedRole) : undefined,
        name: searchTerm || undefined,
        published: getPublishedFilter(selectedStatus),
        orderBy: pageConfig.orderBy,
        order: pageConfig.order,
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch data');
    }
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.orderBy,
    pageConfig.order,
    selectedRole,
    searchTerm,
    selectedStatus,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Data now comes from server-side pagination
  const displayData = paginatedData || {
    content: [],
    totalElements: 0,
    totalPages: 0,
  };

  const handleSave = async (data: IKeyActivity) => {
    if (!currentTenant?.id) return;

    try {
      if (formMode === 'edit' && selectedRow?.id) {
        await updateKeyActivity(currentTenant.id, selectedRow.id, {
          name: data.name,
          org_chart_node_id: Number(data.org_chart_node_id),
          is_published: data.is_published ?? false,
        });
      } else {
        await createKeyActivity(currentTenant.id, {
          name: data.name,
          org_chart_node_id: Number(data.org_chart_node_id),
          is_published: data.is_published ?? false,
        });
      }

      setFormMode(null);
      setSelectedRow(null);
      reset({ name: '', org_chart_node_id: undefined });
      await getKeyActivities(currentTenant.id, {
        page: pageConfig.page,
        page_size: pageConfig.limit,
        org_chart_node_id: selectedRole ? Number(selectedRole) : undefined,
        name: searchTerm || undefined,
        published: getPublishedFilter(selectedStatus),
        orderBy: pageConfig.orderBy,
        order: pageConfig.order,
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to save key activity');
    }
  };

  const handleDelete = async (row: IKeyActivity) => {
    if (!currentTenant?.id || !row.id) return;
    await deleteKeyActivity(currentTenant.id, row.id);
    await getKeyActivities(currentTenant.id, {
      page: pageConfig.page,
      page_size: pageConfig.limit,
      org_chart_node_id: selectedRole ? Number(selectedRole) : undefined,
      name: searchTerm || undefined,
      published: getPublishedFilter(selectedStatus),
      orderBy: pageConfig.orderBy,
      order: pageConfig.order,
    });
  };

  const allRoles = flattenTree(orgCharts);
  const roleOptions = allRoles.map(node => ({
    label: node.name,
    value: String(node.id),
  }));

  const columns: MainTableColumn[] = [
    {
      name: 'role',
      label: 'Role',
      render: (_: unknown, row: IKeyActivity) => row?.org_chart_node?.name ?? '-',
    },
    { name: 'name', label: 'Name' },
    {
      name: 'is_published',
      label: 'Publish',
      render: (val: boolean) =>
        val ? (
          <span className="text-green-700 bg-green-100 border inline-block border-green-300 px-2 rounded-lg">
            Published
          </span>
        ) : (
          <span className="text-red-700 bg-red-100 border inline-block border-red-300 px-2 rounded-lg">
            Unpublished
          </span>
        ),
    },
  ];

  const fields = [
    {
      name: 'org_chart_node_id',
      label: 'Role',
      type: 'select',
      required: true,
      options: roleOptions,
      placeholder: 'Select role',
    },
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter activity name',
    },
  ];

  return (
    <section className="w-full">
      <CompanyHeader
        title="Key Activities"
        description="Define the main activities your company performs to deliver value."
        onCreate={() => {
          reset({ name: '' });
          setFormMode('add');
        }}
        onPublish={async value => {
          if (!currentTenant?.id) return;
          await bulkPublishKeyActivities(currentTenant.id, value);
          await getKeyActivities(currentTenant.id, {
            page: pageConfig.page,
            page_size: pageConfig.limit,
            org_chart_node_id: selectedRole ? Number(selectedRole) : undefined,
            name: searchTerm || undefined,
            published: getPublishedFilter(selectedStatus),
          });
        }}
        isPublished={allPublished}
      />

      <div className="mx-5 border-b border-gray-200" />

      {formMode && orgCharts.length > 0 && (
        <FormProvider {...formControl}>
          <form ref={formRef} onSubmit={handleSubmit(handleSave)} className="mt-4">
            <FormBuilderWithLayout
              description={formMode === 'add' ? 'Create Key Activity' : 'Edit Key Activity'}
              fields={fields}
              onSubmit={handleSubmit(handleSave)}
              onCancel={() => {
                setFormMode(null);
                setSelectedRow(null);
              }}
              submitLabel={formMode === 'add' ? 'Save' : 'Update'}
              cancelLabel="Cancel"
              showPublishButton={true}
            />
          </form>
        </FormProvider>
      )}

      <div className="flex justify-start mt-5 px-5 gap-2">
        <SearchBar
          value={searchTerm}
          onChange={val => {
            setSearchTerm(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
          placeholder="Search activities..."
          className="w-72"
        />
        <FilterSelect
          label="Role"
          options={roleOptions}
          value={selectedRole}
          onChange={val => {
            setSelectedRole(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
        <FilterSelect
          label="Status"
          options={[
            { label: 'Published', value: 'Published' },
            { label: 'Unpublished', value: 'Unpublished' },
          ]}
          value={selectedStatus}
          onChange={val => {
            setSelectedStatus(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
        <ClearFilterButton
          onClear={() => {
            setSearchTerm('');
            setSelectedRole('');
            setSelectedStatus('');
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
      </div>

      <MainTable
        showHeader={false}
        isLoading={isLoading}
        data={{
          content: displayData.content as unknown as { [key: string]: unknown }[],
          totalElements: displayData.totalElements,
          totalPages: displayData.totalPages,
        }}
        columns={columns}
        pageConfig={pageConfig}
        setPageConfig={setPageConfig}
        onEdit={row => {
          reset(row);
          setFormMode('edit');
          setSelectedRow(row);
        }}
        onDelete={handleDelete}
        showActions
        useConfirmDelete={true}
      />
    </section>
  );
}
